"use client";

import { useState } from "react";
import { toast } from "sonner";
import { useRouter } from "@bprogress/next";
import {
  ArrowRightIcon,
  BookOpenIcon,
  MoreHorizontalIcon,
  RotateCcwIcon,
  TrashIcon,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { createPath } from "@/service/routing/routing";
import { clientDel, clientPatch } from "@/service/client-api/client-api";
import { SuperLink as Link } from "@/components/common/super-link";
import { PictureAsset } from "@/components/common/picture-asset";
import { getAssetImage } from "@/service/image/asset";
import { Button } from "@/components/ui/button";
import { DeleteModal } from "@/components/common/delete-modal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * Kafelek zestawu do przeglądania — celowo trzyma się kształtu ExamAttemptCard (ta sama
 * szerokość, ramka i cień), bo oba slidery stoją jeden pod drugim na stronie głównej.
 * Zamiast grafiki egzaminu mamy pasek postępu przewertowania, bo tu nie ma wyniku.
 */
export const ExamBrowseSessionCard = ({
  session,
  fluid = false,
}: {
  session: ExamQuestionBrowseSessionVM;
  fluid?: boolean;
}) => {
  const router = useRouter();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const { id, label, questionsCount, lastViewedQuestion, illustrationKey } =
    session;

  const path = createPath("exam.browse.session", { session: id });

  const progress =
    questionsCount > 0
      ? Math.min(100, Math.round((lastViewedQuestion / questionsCount) * 100))
      : 0;

  return (
    <div
      className={cn(
        "flex flex-col gap-4 rounded-lg border border-zinc-100 bg-white p-3 shadow-[4px_8px_19px_0px_rgba(199,44,199,0.15)]",
        fluid ? "w-full" : "w-[340px] shrink-0",
      )}
    >
      <div className="flex w-full flex-col gap-2">
        {/* Grafika i jej proporcje jak na karcie podejścia — oba slidery stoją obok siebie. */}
        <div className="relative h-[105px] w-full overflow-hidden rounded-[5px] bg-zinc-100">
          <PictureAsset
            source={getAssetImage(`/exams/illustrations/${illustrationKey}`)}
            ext="png"
            alt={label}
            className="h-full w-full object-cover"
            wrapperClassName="block h-full w-full"
          />
          <span className="absolute top-2 left-2 flex items-center gap-1 rounded-[17px] bg-[#f9e2f9] px-2 py-0.5 text-xs font-semibold text-[#8a1f8a]">
            <BookOpenIcon size={12} />
            {progress}%
          </span>
          <div className="absolute inset-x-0 bottom-0 h-1.5 bg-black/10">
            <div
              className="h-full bg-[#C72CC7]"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="flex w-full flex-col">
          <div className="flex w-full items-center justify-between gap-2">
            <h3
              data-title={label}
              className="text-card-foreground truncate text-base font-semibold"
            >
              {label}
            </h3>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Akcje zestawu do przeglądania"
                  className="text-muted-foreground hover:text-foreground size-8 shrink-0"
                >
                  <MoreHorizontalIcon size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link href={path}>
                    <ArrowRightIcon size={16} />
                    Kontynuuj
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={lastViewedQuestion === 1}
                  onSelect={async () => {
                    await clientPatch("exam.browse.reset", { session: id });
                    toast.info("Wrócono na początek zestawu");
                    router.refresh();
                  }}
                >
                  <RotateCcwIcon size={16} />
                  Od początku
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => {
                    setTimeout(() => setIsDeleteOpen(true), 0);
                  }}
                  className="text-red-600 focus:text-red-600"
                >
                  <TrashIcon size={16} className="text-red-600" />
                  Usuń
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <p className="text-muted-foreground text-sm font-medium">
            Pytanie {lastViewedQuestion} z {questionsCount}
          </p>
        </div>
      </div>

      <Button variant="purple" asChild className="w-full">
        <Link href={path}>
          Kontynuuj
          <ArrowRightIcon />
        </Link>
      </Button>

      <DeleteModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onDelete={() => {
          clientDel("exam.browse.delete", { session: id }).then(() => {
            toast.info("Zestaw usunięty");
            setIsDeleteOpen(false);
            router.refresh();
          });
        }}
        resourceName="zestaw do przeglądania"
      />
    </div>
  );
};
